import axios from 'axios';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { useEffect, useState, useContext, useRef, Fragment, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ChatMessage, { Message } from './ChatMessage';
import { PromptContext } from '../../context/PromptContext';
import { LoadingContext } from '../../context/LoadingContext';
import { SettingContext } from '../../context/SettingContext';
import { AuthContext } from '@/context/AuthContext';

import { getLeadProfile } from '../../services/profile';

import { useHistoryFunctions } from '../../providers/historyProvider';
import { encryptAesWithSha256, formatInput, formatMarkdown } from '../../utils/chat';
import {
  CHAT_DISABLED_ID,
  ELYSIA_GUARDRAIL_ERROR_MESSAGE,
  ELYSIA_INTERNAL_ERROR_MESSAGE,
} from '../../config/settings';
import { toast } from 'react-toastify';
import ReactMarkdown from 'react-markdown';
import useElysiaStore from '@/app/elysia-store';
import { getBrandDetails } from '@/services/brand';

// Initialize the variables to handle api abort, cancel streaming
let cancelStreaming = false;
let isStreaming = false;
let controller = new AbortController();
let signal = controller.signal;
let metadataSourceList = [];
const toolCallKey = 'toolCall';
let isElysiaError = false;

// Thinking and SQL tracking variables
let currentSectionIndex = 0;
let isInThinkingBlock = false;
let hasSeenTextChunk = false;
let currentToolInput = '';
let currentResponseBlocks: ResponseBlock[] = [];

const ChatSession = ({ contentRef }) => {
  const division = useElysiaStore((state) => state.division);
  const persona = useElysiaStore((state) => state.persona);
  const name = useElysiaStore((state) => state.name);
  const brand = useElysiaStore((state) => state.brand);
  const brandDetails = useElysiaStore((state) => state.brandDetails);
  const isPreview = useElysiaStore((state) => state.isPreview);
  const privewApiPayload = useElysiaStore((state) => state.privewApiPayload);
  const allDivisions = useElysiaStore((state) => state.allDivisions);
  const brandDoc = useElysiaStore((state) => state.brandDoc);
  const appPreviewApiPayload = useElysiaStore((state) => state.appPreviewApiPayload);
  const isAppPreview = useElysiaStore((state) => state.isAppPreview);
  const aiAppId = useElysiaStore((state) => state.aiAppId);
  const imageGenPayload = useElysiaStore((state) => state.imageGenPayload);
  const imageLoading = useElysiaStore((state) => state.imageLoading);
  const activeChatHistoryRecord = useElysiaStore((state) => state.activeChatHistoryRecord);
  const chatSessionId = useElysiaStore((state) => state.chatSessionId);
  const newChat = useElysiaStore((state) => state.newChat);
  const clearState = useElysiaStore((state) => state.clearState);
  const chatAgent = useElysiaStore((state) => state.chatAgent);
  const context = useElysiaStore((state) => state.context);
  const contextName = useElysiaStore((state) => state.contextName);
  const sources = useElysiaStore((state) => state.sources);
  const nextBestActions = useElysiaStore((state) => state.nextBestActions);
  const entryPoint = useElysiaStore((state) => state.entryPoint);
  const includeSearch = useElysiaStore((state) => state.includeSearch);
  const isChatActive = useElysiaStore((state) => state.isChatActive);
  const isPrivateChat = useElysiaStore((state) => state.isPrivateChat);
  const isStreamingStopped = useElysiaStore.getState().isStreamingStopped;
  const localToolResultKey = useElysiaStore((state) => state.localToolResultKey) || 'toolResult';
  const localAutoScrollEnabledKey =
    useElysiaStore((state) => state.localAutoScrollEnabledKey) || 'autoScrollEnabled';
  const availablePrivateDocs = useElysiaStore((state) => state.availablePrivateDocs);
  const historyCollectionName = useElysiaStore((state) => state.historyCollectionName);
  const dataAgentDocs = useElysiaStore((state) => state.dataAgentDocs);
  const profitCenter = useElysiaStore((state) => state.profitCenter);
  const financeAppLocation = useElysiaStore((state) => state.financeAppLocation);

  const setDivision = useElysiaStore.getState().setDivision;
  const setBrand = useElysiaStore.getState().setBrand;
  const setPersona = useElysiaStore.getState().setPersona;
  const setBrandDetails = useElysiaStore.getState().setBrandDetails;
  const setAiAppId = useElysiaStore.getState().setAiAppId;
  const setUploadedImageId = useElysiaStore.getState().setUploadedImageId;
  const setImageGenPayload = useElysiaStore.getState().setImageGenPayload;
  const setImageLoading = useElysiaStore.getState().setImageLoading;
  const setIsStreamingRequested = useElysiaStore.getState().setIsStreamingRequested;
  const setNewChat = useElysiaStore.getState().setNewChat;
  const setClearState = useElysiaStore.getState().setClearState;
  const setIsNewSession = useElysiaStore.getState().setIsNewSession;
  const setSessionPath = useElysiaStore.getState().setSessionPath;
  const setBackToChatSession = useElysiaStore.getState().setBackToChatSession;
  const setIsChatActive = useElysiaStore.getState().setIsChatActive;
  const setIsChatDisabled = useElysiaStore.getState().setIsChatDisabled;
  const setDynamicStreamCount = useElysiaStore.getState().setDynamicStreamCount;
  const setFinanceAppLocation = useElysiaStore.getState().setFinanceAppLocation;
  const setProfitCenter = useElysiaStore.getState().setProfitCenter;

  const navigate = useNavigate();
  const { prompt, setPrompt, isProcessed, setProcessed } = useContext(PromptContext);
  const { profileInfo } = useContext(AuthContext);
  const { setIsLoading } = useContext(LoadingContext);
  const activeChatHistoryConversation = activeChatHistoryRecord?.history || [];
  const historyFunctions = useHistoryFunctions();
  const [isStreamCompleted, setIsStreamCompleted] = useState<boolean>(false);
  const [responseImageId, setResponseImageId] = useState<string>('');
  const [pptConfig, setPptConfig] = useState({});
  const location = useLocation();
  const {
    defaultLanguage,
    defaultModel,
    selectedLanguage,
    selectedModel,
    languageUsedForResponse,
    modelUsedForResponse,
    setAvailableImageQuota,
  } = useContext(SettingContext);

  // Thinking and SQL state
  const [sqlSections, setSqlSections] = useState<any[]>([]);

  // New: State for interleaved thinking/text blocks
  const [responseBlocks, setResponseBlocks] = useState<ResponseBlock[]>([]);

  if (!historyFunctions) {
    throw new Error('History functions are not available.');
  }
  const { addChatToToday, updateSessionTitle, updateChatHistoryOnDelete } = historyFunctions;
  const {
    modelProvider,
    modelName,
    personality,
    creativity,
    role,
    domainExpertise,
    writingStyle,
    outputLanguage,
    showSourceList,
  } = useContext(SettingContext);
  const [chatMessages, setChatMessages] = useState<Message[]>([]);
  const [result, setResult] = useState<string | null>(null);
  const [toolCall, setToolCall] = useState('');
  const [toolResult, setToolResults] = useState<any[]>([]);
  const [chartConfig, setChartConfig] = useState<any | null>(null);
  const { allModels, setSelectedLanguage, setSelectedModel } = useContext(SettingContext);
  const scrollRef = useRef<HTMLDivElement>(null);
  const virtuosoRef = useRef<VirtuosoHandle | null>(null);
  const resultScrollRef = useRef<HTMLDivElement>(null);
  const [runId, setRunId] = useState<string>('');
  const models = useRef({});
  //if any of the setting context changes - get notified, but do nothing immediately
  useEffect(() => {
    //do nothing immediately
  }, [
    modelProvider,
    modelName,
    personality,
    creativity,
    role,
    domainExpertise,
    writingStyle,
    outputLanguage,
    showSourceList,
    includeSearch,
  ]);

  const combinedMessages = useMemo(() => {
    if (!isChatActive) return chatMessages;

    if (isStreaming && (result || toolCall || responseBlocks.length > 0)) {
      return [
        ...chatMessages,
        {
          type: 'System',
          body: result,
          toolCall,
          toolResult,
          imageId: responseImageId,
          chartConfig,
          imageLoading,
          responseBlocks: responseBlocks,
          sqlSections: sqlSections,
          isElysiaError: isElysiaError,
        } as Message,
      ];
    }
    return chatMessages;
  }, [
    chatMessages,
    isChatActive,
    result,
    toolCall,
    toolResult,
    responseImageId,
    chartConfig,
    imageLoading,
    responseBlocks,
    sqlSections,
  ]);

  //if agent changes, clear state and start over again
  useEffect(() => {
    setPrompt?.('');
    if (clearState || isStreamingStopped === true) {
      cancelStreaming = true;
      // abort the api when streaming isn't started yet
      if (!isStreaming) {
        controller.abort();
        setIsStreamingRequested?.(false);
      }
      if (!isStreamingStopped) {
        setChatMessages([]);
      }
    }
    // eslint-disable-next-line
  }, [
    setPrompt,
    clearState,
    chatAgent,
    context,
    contextName,
    nextBestActions,
    activeChatHistoryRecord,
    isStreamingStopped,
  ]);

  //if prompt from text input changes, react based on the chosen agent (in context)
  useEffect(() => {
    if (prompt && prompt !== '' && !isProcessed) {
      if (prompt !== '' && !prompt.startsWith('auto-trigger-prompt')) {
        const newMessage: Message = {
          type: 'User',
          body: prompt,
        };
        setChatMessages((prevArray) => [...prevArray, newMessage]);
        setIsChatActive?.(true);
      }
      processPrompt();
      setProcessed?.(true);
    }
    //eslint-disable-next-line
  }, [prompt, isProcessed, setProcessed, setIsLoading]);

  //if chat history is updated in the current chat session
  useEffect(() => {
    if (
      (chatMessages.length || activeChatHistoryRecord?.session_id) &&
      sessionStorage.getItem(localAutoScrollEnabledKey) === 'true'
    ) {
      const isMobile = /iPhone|iPad|Android/i.test(navigator.userAgent);
      if (isMobile) {
        setTimeout(() => {
          scrollRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'end',
            inline: 'start',
          });
        }, 50);
      } else {
        // Desktop scrolling
        scrollRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
          inline: 'start',
        });
      }
    }
  }, [chatMessages.length, activeChatHistoryRecord?.session_id, localAutoScrollEnabledKey]);

  // when history load land at bottom of the chat in virtuoso
  useEffect(() => {
    if (
      activeChatHistoryRecord?.session_id &&
      sessionStorage.getItem(localAutoScrollEnabledKey) === 'true'
    ) {
      const delay = /iPhone|iPad|Android/i.test(navigator.userAgent) ? 300 : 250;
      setTimeout(() => {
        virtuosoRef.current?.scrollToIndex({ index: 'LAST', align: 'end' });
      }, delay);
    }
    // eslint-disable-next-line
  }, [activeChatHistoryRecord?.session_id, activeChatHistoryConversation.length]);

  // abort/cancel the streaming api on component unmount
  useEffect(() => {
    return () => {
      cancelStreaming = true;
      if (!isStreaming) {
        controller.abort();
        setIsStreamingRequested?.(false);
      }
      setIsChatActive?.(false);
      setChatMessages([]);
    };
    // eslint-disable-next-line
  }, [setIsStreamingRequested]);

  // If private chat mode changes, clear chat messages
  useEffect(() => {
    setChatMessages([]);
  }, [isPrivateChat]);

  useEffect(() => {
    if (isStreamCompleted) {
      appendMesgToChatHistory(
        result,
        runId,
        localStorage.getItem(localToolResultKey),
        localStorage.getItem(toolCallKey)
      );
    }
    // eslint-disable-next-line
  }, [result, isStreamCompleted, runId]);

  useEffect(
    () => {
      const documentsInChatSession = activeChatHistoryConversation[0]?.references?.map((doc) =>
        doc?.title.toString()
      );
      const agentName = activeChatHistoryRecord?.metadata?.agent_name;
      if (dataAgentDocs && dataAgentDocs.length > 1 && agentName === 'Data Agent') {
        const isDocumentDeleted = documentsInChatSession.some(
          (doc) => !dataAgentDocs?.includes(doc)
        );
        setIsChatDisabled?.(isDocumentDeleted);
      }
    }, //eslint-disable-next-line
    [dataAgentDocs]
  );

  useEffect(() => {
    const agentName = activeChatHistoryRecord?.metadata?.agent_name;
    if (agentName === 'Data Agent') return;
    setIsChatDisabled?.(false);
    if (!historyCollectionName || !activeChatHistoryConversation?.length || !availablePrivateDocs)
      return;
    const isPrivateCollection = historyCollectionName?.includes('private');
    if (!isPrivateCollection) return;
    const documentsInChatSession = activeChatHistoryConversation[0]?.references?.map((doc) =>
      doc?.title.toString()
    );
    if (!documentsInChatSession?.length) return;
    if (availablePrivateDocs.length > 0) {
      const isDocumentDeleted = documentsInChatSession.some(
        (doc) => !availablePrivateDocs?.includes(doc)
      ); // will return true if a document is not found in the private collection
      setIsChatDisabled?.(isDocumentDeleted);
    }

    // eslint-disable-next-line
  }, [activeChatHistoryConversation, availablePrivateDocs, historyCollectionName]);

  useEffect(() => {
    toast.dismiss(CHAT_DISABLED_ID);
  }, [location]);

  useEffect(() => {
    allModels.forEach((model) => {
      models.current[`${model.name_of_model}`] = model.display_name;
    });
  }, [allModels]);

  useEffect(() => {
    if (
      activeChatHistoryRecord?.metadata?.response_language &&
      activeChatHistoryRecord?.metadata?.name_of_model
    ) {
      setSelectedLanguage(activeChatHistoryRecord?.metadata?.response_language);
      const responseModel = allModels.find(
        (model) => model.name_of_model === activeChatHistoryRecord?.metadata?.name_of_model
      );
      if (responseModel) {
        setSelectedModel(responseModel);
      } else {
        setSelectedModel(allModels[0]);
      }
    }
    //eslint-disable-next-line
  }, [activeChatHistoryRecord, allModels]);

  useEffect(() => {
    // checking if the history is a brand-agent and setting preferences
    const setBrandItems = async () => {
      const brandsForDiv = allDivisions.find(
        (div) => div.division === activeChatHistoryRecord?.metadata?.division
      );

      if (brandsForDiv && brandsForDiv?.brand?.length) {
        const selectedBrand = brandsForDiv.brand.find(
          (brand) => brand.name === activeChatHistoryRecord?.metadata?.brand
        );
        if (selectedBrand?.id?.length) {
          setBrand(selectedBrand);
          const brandDetails = await getBrandDetails(selectedBrand.id);
          setBrandDetails(brandDetails);
          const allPersonaDetails = brandDetails?.communication?.target_audiences;
          if (activeChatHistoryRecord?.metadata?.persona_id?.length) {
            const selectedPersona = allPersonaDetails.find(
              (persona) => persona.id === activeChatHistoryRecord?.metadata?.persona_id
            );
            setPersona(selectedPersona);
          }
        }
      }
    };

    const setImageGentItems = async () => {
      if (activeChatHistoryRecord?.metadata?.agent_metadata?.image_generation_agent) {
        const data = activeChatHistoryRecord?.metadata?.agent_metadata?.image_generation_agent;
        setImageGenPayload({
          aspect_ratio: data?.aspect_ratio,
          color_palette: data?.color_palette,
          // negative_prompt: data.negative_prompt,
          starter_image_id: data?.starter_image_id,
          image_quality: data?.image_quality,
        });
        setUploadedImageId(data.starter_image_id ?? '');
      }
    };

    const initializeChatSession = async () => {
      if (activeChatHistoryRecord?.metadata?.agent_id?.length) {
        setAiAppId(activeChatHistoryRecord?.metadata?.agent_id);
        setDivision(activeChatHistoryRecord?.metadata?.division ?? '');
        await setBrandItems(); // Wait for the async function to complete
      }
      if (activeChatHistoryRecord?.metadata?.agent_metadata?.image_generation_agent) {
        await setImageGentItems();
      }

      if (
        activeChatHistoryRecord?.metadata?.profit_center ||
        activeChatHistoryRecord?.metadata?.location
      ) {
        const profitCenterData: ProfitCenter = {
          value: activeChatHistoryRecord?.metadata?.profit_center?.id || '',
          level: activeChatHistoryRecord?.metadata?.profit_center?.level || 0,
          label: activeChatHistoryRecord?.metadata?.profit_center?.id || '',
        };
        const LocationData = {
          value: activeChatHistoryRecord?.metadata?.location?.id || '',
          level: activeChatHistoryRecord?.metadata?.location?.level || 0,
          label: activeChatHistoryRecord?.metadata?.location?.id || '',
        };
        setProfitCenter(profitCenterData);
        setFinanceAppLocation(LocationData);
      }
    };

    initializeChatSession(); // Call the async initializer
    // add default model as dependency as it chnages selected model in provider

    //eslint-disable-next-line
  }, [activeChatHistoryRecord, allDivisions, defaultModel]);

  useEffect(() => {
    if (isPreview || isAppPreview) {
      setChatMessages([]);
    }
    //eslint-disable-next-line
  }, [brand, isAppPreview]);

  const appendMesgToChatHistory = (message, runId, srcList, searchTxt) => {
    const newMessage: Message = {
      type: 'System',
      body: message,
      showInteractions: runId && runId !== '' ? true : false,
      prompts:
        chatAgent === 'Sales' && entryPoint === 'upload' && nextBestActions ? nextBestActions : [],
      runId: runId,
      references: metadataSourceList,
      toolResult: srcList ? JSON.parse(srcList) : [],
      toolCall: searchTxt,
      modelUsed: modelUsedForResponse?.current?.display_name,
      languageUsed: languageUsedForResponse?.current,
      imageId: responseImageId,
      chartConfig: chartConfig,
      pptConfig: pptConfig,
      responseBlocks: responseBlocks,
      sqlSections: sqlSections,
      isElysiaError: isElysiaError,
    };
    setResult('');
    setToolCall('');
    setToolResults([]);
    setIsLoading(false);
    setIsStreamCompleted(false);
    setResponseImageId('');
    setChartConfig(null);
    setPptConfig({});
    setResponseBlocks([]);
    currentResponseBlocks = [];
    metadataSourceList = [];
    localStorage.setItem(toolCallKey, '');
    localStorage.removeItem(localToolResultKey);
    setRunId('');

    // Clear chat messages list on abort
    if (cancelStreaming && isStreamingStopped !== true) {
      setChatMessages([]);
      setIsChatActive?.(false);
    } else {
      setChatMessages((prevArray) => [...prevArray, newMessage]);
      setIsChatActive?.(true);
    }
  };

  const processStreamingRequest = async () => {
    const url = getStreamingServiceUrl();
    const payload = await getStreamingServicePayload();
    const auth = axios.defaults.headers.common['Authorization'];
    sessionStorage.setItem(localAutoScrollEnabledKey, 'true');
    cancelStreaming = false;
    isStreaming = false;
    controller = new AbortController();
    signal = controller.signal;
    metadataSourceList = [];
    setClearState?.(false);

    currentSectionIndex = 0;
    isInThinkingBlock = false;
    hasSeenTextChunk = false;
    currentToolInput = '';
    setSqlSections([]);
    currentResponseBlocks = [];
    setResponseBlocks([]);
    isElysiaError = false;

    if (name === 'Image Generator') {
      setImageLoading(true);
    }

    try {
      useElysiaStore.setState({ isStreamingStopped: false });
      setIsStreamingRequested?.(true);
      const response = await fetch(url, {
        method: 'POST',
        signal: signal,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `${auth}`,
        },
        body: JSON.stringify(payload),
      });
      if (newChat && !isPrivateChat && !isPreview && !isAppPreview) {
        const chat = {
          chat_history_title: 'New Chat',
          is_archived: null,
          session_id: String(chatSessionId),
          updated_on: null,
          is_new_chat: true,
        };
        addChatToToday(chat);
        setBackToChatSession?.(false);
      }

      const reader = response?.body?.getReader();
      const decoder = new TextDecoder('utf-8');

      // 1. Initialize buffer for partial lines
      let buffer = '';

      reader?.read().then(function processResult(res) {
        isStreaming = true;
        setDynamicStreamCount?.((prev) => prev + 1);

        // Cancel the streaming instead of abort if streaming is already started
        if (cancelStreaming) {
          reader?.cancel();
          setIsStreamingRequested?.(false);
          if (newChat) {
            updateChatHistoryOnDelete(String(chatSessionId));
          }
          if (name === 'Image Generator') {
            setToolCall('');
            localStorage.setItem(toolCallKey, '');
          }
        }
        if (res.done) {
          setIsStreamCompleted(true);
          setIsStreamingRequested?.(false);
          return;
        }

        const chunk = decoder.decode(res.value, { stream: true });
        const combined = buffer + chunk;
        const tokens = combined.split('\n');

        buffer = tokens.pop() || '';

        let ansTokenCat: string = '';
        let metaToken: any = {};
        let event = '';

        tokens.forEach((line) => {
          if (!line.trim()) return;

          if (line.startsWith('event:')) {
            event = line.slice(6).trim();
          } else if (line.startsWith('data:')) {
            const jsonData = line.substring(5);

            let pLine;
            try {
              pLine = JSON.parse(jsonData || '{}');
            } catch (e) {
              console.error('JSON Parse Error (skipping line):', e, 'Line:', line);
              return;
            }

            const getLastBlock = () => currentResponseBlocks[currentResponseBlocks.length - 1];

            if (event === 'text_chunk') {
              if (!hasSeenTextChunk) {
                hasSeenTextChunk = true;
              }
              ansTokenCat += pLine?.answer;

              const lastBlock = getLastBlock();
              if (lastBlock && lastBlock.type === 'text') {
                lastBlock.content += pLine?.answer;
              } else {
                currentResponseBlocks.push({ type: 'text', content: pLine?.answer });
              }
            } else if (event === 'content_block_start' && pLine?.type === 'thinking') {
              isInThinkingBlock = true;

              const lastBlock = getLastBlock();
              if (lastBlock && lastBlock.type === 'thinking') {
                lastBlock.content += '\n\n===SECTION_BREAK===\n\n';
                lastBlock.content += 'block-start\n';
              } else {
                currentResponseBlocks.push({ type: 'thinking', content: 'block-start\n' });
              }

              if (hasSeenTextChunk) {
                currentSectionIndex++;
                hasSeenTextChunk = false;
              }

              if (sessionStorage.getItem(localAutoScrollEnabledKey) === 'true') {
                resultScrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
              }
            } else if (event === 'thinking' && pLine?.query && isInThinkingBlock) {
              const lastBlock = getLastBlock();
              if (lastBlock && lastBlock.type === 'thinking') {
                lastBlock.content += pLine.query;
              } else {
                currentResponseBlocks.push({ type: 'thinking', content: pLine.query });
              }
              if (sessionStorage.getItem(localAutoScrollEnabledKey) === 'true') {
                resultScrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
              }
            } else if (event === 'content_block_end' && pLine?.type === 'thinking') {
              isInThinkingBlock = false;

              const lastBlock = getLastBlock();
              if (lastBlock && lastBlock.type === 'thinking') {
                lastBlock.content += '\nblock-end\n';
              }
            } else if (event === 'content_block_start' && pLine?.type === 'tool_call') {
              currentToolInput = '';
            } else if (event === 'tool_input' && pLine?.args_delta) {
              currentToolInput += pLine.args_delta;
            } else if (event === 'tool_output' && pLine?.result) {
              try {
                let purpose = '';
                let sqlQuery = '';

                try {
                  const parsedInput = JSON.parse(currentToolInput);
                  purpose = parsedInput.purpose || '';
                  sqlQuery = parsedInput.query || '';
                } catch (parseError) {
                  const purposeMatch = currentToolInput.match(/"purpose":\s*"([^"]*)"/);
                  const queryMatch = currentToolInput.match(/"query":\s*"((?:[^"\\]|\\.)*)"/);

                  purpose = purposeMatch ? purposeMatch[1] : '';
                  sqlQuery = queryMatch ? queryMatch[1] : '';
                  console.error(parseError);
                }

                sqlQuery = sqlQuery
                  .replace(/\\n/g, '\n')
                  .replace(/\\"/g, '"')
                  .replace(/\\\\/g, '\\');

                const resultData = JSON.parse(pLine.result);
                const formattedResult = JSON.stringify(resultData, null, 2);

                setSqlSections((prev) => {
                  const sections = [...prev];

                  let section = sections.find((s) => s.sectionIndex === currentSectionIndex);

                  if (!section) {
                    section = {
                      sectionIndex: currentSectionIndex,
                      intermediate: [],
                      final: [],
                    };
                    sections.push(section);
                  }

                  const step = {
                    label: `Step ${(purpose === 'intermediate' ? section.intermediate.length : section.final.length) + 1}`,
                    code: sqlQuery,
                    result: formattedResult,
                  };

                  if (purpose === 'intermediate') {
                    section.intermediate.push(step);
                  } else if (purpose === 'final') {
                    section.final.push(step);
                  }
                  return sections;
                });
              } catch (e) {
                console.error('Error parsing tool result:', e);
              }
            } else if (event === 'error') {
              setImageLoading(false);
              isElysiaError = true;
              const errorMessage = pLine?.message ?? pLine?.answer ?? '';
              ansTokenCat += errorMessage;
            } else if (event === 'chartjs') {
              const newChartConfig = {
                ...pLine.chartjs,
                _id: Date.now() + Math.random().toString(36).substring(2, 8),
              };
              setChartConfig(newChartConfig);
            } else if (event === 'ppt') {
              setPptConfig(pLine?.answer);
            } else if (event === 'refresh_info') {
              const lastBlock = getLastBlock();
              lastBlock.content += pLine?.answer;
            } else if (event === 'metadata') {
              metaToken = pLine?.metadata;
            } else if (event === 'final_response' && ansTokenCat === '' && !hasSeenTextChunk) {
              ansTokenCat += pLine?.answer;
              isElysiaError = true;
            } else if (event === 'image') {
              setResponseImageId(pLine?.image);
            } else if (event === 'tool_results') {
              const toolRes = [
                { toolcall: true, searching: localStorage.getItem(toolCallKey) },
                ...pLine?.tool_results,
              ];

              setToolResults(toolRes);
              localStorage.setItem(localToolResultKey, JSON.stringify(toolRes));
            } else if (event === 'tool_call' || event === 'intermediate_step') {
              setToolCall(pLine?.args?.query);
              localStorage.setItem(toolCallKey, pLine?.args?.query);
              setTimeout(() => {
                if (sessionStorage.getItem(localAutoScrollEnabledKey) === 'true') {
                  resultScrollRef.current?.scrollIntoView(true);
                }
              }, 200);
            }
          }
        });

        // Update Block State
        if (currentResponseBlocks.length > 0) {
          setResponseBlocks([...currentResponseBlocks]);
        }

        if (ansTokenCat) {
          setResult((prev) => prev + ansTokenCat);
          if (sessionStorage.getItem(localAutoScrollEnabledKey) === 'true') {
            resultScrollRef.current?.scrollIntoView(true);
          }
          if (tokens.includes(ELYSIA_INTERNAL_ERROR_MESSAGE)) {
            isElysiaError = true;
            if (newChat) {
              updateChatHistoryOnDelete(String(chatSessionId));
            }
          } else if (tokens.includes(ELYSIA_GUARDRAIL_ERROR_MESSAGE)) {
            if (newChat) {
              updateChatHistoryOnDelete(String(chatSessionId));
            }
          }
        }
        if (metaToken && Object.keys(metaToken).length) {
          try {
            metadataSourceList = metaToken?.references;
            setRunId(metaToken?.run_id);
            if (metaToken?.image_monthly_quota) {
              setAvailableImageQuota(metaToken?.image_monthly_quota_remaining);
            }
            if (metaToken?.agent_metadata?.image_generation_agent?.image_model_name) {
              modelUsedForResponse.current = {
                display_name: metaToken?.agent_metadata?.image_generation_agent?.image_model_name,
              };
              languageUsedForResponse.current = null;
            }
            if (sessionStorage.getItem(localAutoScrollEnabledKey) === 'true') {
              resultScrollRef.current?.scrollIntoView(true);
            }

            const title = metaToken?.title;
            if (
              !cancelStreaming &&
              title &&
              newChat &&
              isStreamingStopped !== true &&
              !isPrivateChat
            ) {
              updateSessionTitle(String(chatSessionId), title);
              setIsNewSession?.(true);
              setSessionPath?.(`/session/${chatSessionId}`);
              if (window.location.pathname === '/') {
                navigate(`/session/${chatSessionId}`, { replace: true });
              }
              setClearState?.(false);
              setNewChat?.(false);
            }
          } catch (e) {
            isElysiaError = true;
            console.error(e);
          }
        }

        return reader?.read().then(processResult);
      });
    } catch (e: any) {
      isElysiaError = true;
      console.error('error', e);
      setIsStreamingRequested?.(false);
      appendMesgToChatHistory(ELYSIA_INTERNAL_ERROR_MESSAGE, '', '[]', '');
    }
  };

  const processLeadProfileRequest = async () => {
    try {
      const response = await getLeadProfile(prompt);
      appendMesgToChatHistory(response, '', '[]', '');
      return;
    } catch (err) {
      console.error('error fetching profile', err);
    }
  };

  const processPrompt = async () => {
    if (prompt !== '') {
      setIsLoading(true);
      setResult('');
      if (chatAgent === 'Lead') {
        processLeadProfileRequest();
      } else {
        //all other usecases
        if (chatAgent === 'Document') {
          setToolCall(chatAgent);
          localStorage.setItem(toolCallKey, chatAgent);
          setTimeout(() => {
            if (sessionStorage.getItem(localAutoScrollEnabledKey) === 'true') {
              resultScrollRef.current?.scrollIntoView(true);
            }
          }, 200);
        }
        if (brandDoc) {
          setToolCall('Document');
          localStorage.setItem(toolCallKey, 'Document');
          setTimeout(() => {
            if (sessionStorage.getItem(localAutoScrollEnabledKey) === 'true') {
              resultScrollRef.current?.scrollIntoView(true);
            }
          }, 200);
        }
        processStreamingRequest();
      }
    } else {
      alert('Please enter a prompt...');
    }
  };

  const getStreamingServiceUrl = () => {
    return `${import.meta.env.VITE_API_DOMAIN}${import.meta.env.VITE_API_VERSION}/ai/chat/stream/completion`;
  };

  const getStreamingServicePayload = async () => {
    const valPrompt = prompt ? prompt : '';
    const payload = {
      appId:
        name === 'Finance Agent'
          ? `${import.meta.env.VITE_FINANCE_APP_ID}`
          : `${import.meta.env.VITE_ELYSIA_APP_ID}`,
      query: valPrompt,
      model: selectedModel.model,
      tokens: 8192,
      creativity: creativity,
      personality: personality,
      role: role,
      writing_style: writingStyle,
      domain_expertise: domainExpertise,
      chat_session: chatSessionId,
      private_chat: isPrivateChat,
      system_prompt: '',
      concepts: [],
      entities: [],
      business_units: [],
      products: [],
      content_domains: [],
      showSourceList: showSourceList,
      include_search: includeSearch,
      include_metadata: true,
      intermediate_steps: true,
      response_language: selectedLanguage,
      default_response_language: defaultLanguage,
      default_name_of_model: defaultModel.name_of_model,
      name_of_model: selectedModel.name_of_model,
      ...(name === 'Brand Agent' && {
        division: division,
        brand: brand?.name,
        tov_id: brandDetails?.id,
      }),
      ...(name === 'Brand Agent' &&
        !isPreview && { agent_id: brandDetails?.id, persona_id: persona.id }),
      ...(chatAgent === 'app-agent' && !isAppPreview && { agent_id: aiAppId }),
      ...(isPreview && { preview_brand_config: privewApiPayload }),
      ...(isAppPreview && { preview_agent_config: appPreviewApiPayload }),
      ...(name == 'Image Generator' && {
        image_generation_agent_config: imageGenPayload,
      }),
      ...(name == 'Finance Agent' && {
        metadata: {
          context: await encryptAesWithSha256(
            JSON.stringify({
              profit_center: { id: profitCenter?.label, level: profitCenter?.level },
              location: { id: financeAppLocation?.label, level: financeAppLocation?.level },
              output_language: selectedLanguage,
            }),
            profileInfo?.userId
          ),
        },
      }),
    };

    if (chatAgent === 'Document' || brandDoc) {
      if (context && context !== 'myContentsOnly') {
        payload['context'] = context;
        payload['filterMyContentsOnly'] = false;
        if (sources && sources.length > 0) {
          payload['sources'] = sources;
        }
      }
      if (context && context === 'myContentsOnly') {
        payload['filterMyContentsOnly'] = true;
        if (sources && sources.length > 0) {
          payload['sources'] = sources;
        }
      }
      payload.showSourceList = true;
    }
    return payload;
  };

  const buildSqlSectionsFromHistory = (historyBlocks: any[]) => {
    const sectionsMap = new Map<number, any>();

    let localSectionIndex = 0;
    let lastToolInput: any = null;

    for (const block of historyBlocks) {
      if (block.type === 'thinking') {
        localSectionIndex++;
        continue;
      }

      if (block.type === 'tool_input') {
        try {
          const input =
            typeof block.content === 'string'
              ? JSON.parse(block.content)
              : JSON.parse(block.content?.input ?? '{}');

          lastToolInput = {
            purpose: input.purpose ?? 'intermediate',
            query: input.query ?? '',
          };
        } catch {
          lastToolInput = null;
        }
      }

      if (block.type === 'tool_output' && lastToolInput) {
        let result = '';

        try {
          const parsed = JSON.parse(block.content);
          result = JSON.stringify(parsed.results ?? parsed, null, 2);
        } catch {
          result = block.content;
        }

        if (!sectionsMap.has(localSectionIndex)) {
          sectionsMap.set(localSectionIndex, {
            sectionIndex: localSectionIndex,
            intermediate: [],
            final: [],
          });
        }

        const section = sectionsMap.get(localSectionIndex);

        const step = {
          label: `Step ${
            lastToolInput.purpose === 'final'
              ? section.final.length + 1
              : section.intermediate.length + 1
          }`,
          code: lastToolInput.query,
          result,
        };

        if (lastToolInput.purpose === 'final') {
          section.final.push(step);
        } else {
          section.intermediate.push(step);
        }

        lastToolInput = null;
      }
    }

    return Array.from(sectionsMap.values());
  };

  const processResponseBlocks = (rawBlocks: any[]) => {
    if (!rawBlocks || rawBlocks.length === 0) return [];

    const historyResponseBlock = [...rawBlocks];

    for (let blockIndex = 0; blockIndex < historyResponseBlock.length; blockIndex++) {
      const currentBlock = historyResponseBlock[blockIndex];

      if (currentBlock.type === 'thinking') {
        currentBlock.content = 'block-start\n' + currentBlock.content + '\nblock-end';

        let nextIndex = blockIndex + 1;

        while (nextIndex < historyResponseBlock.length) {
          const nextBlock = historyResponseBlock[nextIndex];

          if (nextBlock.type === 'text') break;

          if (nextBlock.type === 'thinking') {
            currentBlock.content +=
              '\n\n===SECTION_BREAK===\n\n' + 'block-start\n' + nextBlock.content + '\nblock-end';

            historyResponseBlock.splice(nextIndex, 1);
            continue;
          }

          nextIndex++;
        }
      }
    }

    return historyResponseBlock;
  };

  return (
    <>
      {/* Default system message when no chat is active */}
      {!isChatActive && !isPrivateChat && (
        <>
          <ChatMessage
            message={{
              type: 'System-Default',
              showInteractions: false,
            }}
          />
        </>
      )}
      {/* History List Virtulized */}
      {activeChatHistoryConversation && activeChatHistoryConversation.length > 0 && (
        <Virtuoso<ChatHistoryConversation>
          ref={virtuosoRef}
          style={{ width: '100%' }}
          data={activeChatHistoryConversation}
          overscan={{ main: 500, reverse: 500 }}
          customScrollParent={contentRef.current ?? undefined}
          initialTopMostItemIndex={activeChatHistoryConversation.length - 1}
          computeItemKey={(index, record) => record?.tracking_id ?? index}
          itemContent={(index, record) => {
            let toolResult: ChatHistoryConversation['intermediate_steps'] = [];
            if (record?.intermediate_steps?.length > 1) {
              toolResult = [
                { toolcall: true, searching: record?.intermediate_steps[0]?.args?.query },
                // @ts-expect-error // fix it later
                ...(record?.intermediate_steps[1]?.tool_results || []),
              ];
            }

            let userInput = '';
            let systemResponse = '';
            let systemImageId: string | null = null;
            let chartConfig: any = null;
            let pptConfigObj: PresentationConfig | null = null;
            let historyResponseBlock: any[] = [];
            let historySqlSections: any[] = [];

            if (typeof record.input === 'string' && typeof record.output === 'string') {
              userInput = record.input;
              systemResponse = record.output;
            } else {
              const input = record.input?.[0];
              const output = record.output?.[0];

              if (input) {
                userInput = input.text || '';
              }

              if (output) {
                if (output.type === 'text') {
                  systemResponse = output.text || '';
                } else if (output.type === 'image') {
                  systemImageId = output.image;
                } else if (output.type === 'chartjs') {
                  chartConfig = output.chartjs;
                  systemResponse = output.text || '';
                }
                if (output.type === 'finance') {
                  try {
                    const rawBlocks = JSON.parse(output.text) || [];
                    historyResponseBlock = processResponseBlocks(rawBlocks);
                    historySqlSections = buildSqlSectionsFromHistory(rawBlocks);
                  } catch (error) {
                    console.error('Error processing finance blocks:', error);
                    historyResponseBlock = [];
                    historySqlSections = [];
                  }
                } else if (output.type === 'ppt') {
                  systemResponse = output.text;
                  pptConfigObj = output.ppt;
                }
              }
            }

            return (
              <div>
                <ChatMessage
                  message={{
                    body: formatInput(userInput),
                    type: 'User',
                  }}
                />
                <ChatMessage
                  message={{
                    body: systemResponse,
                    responseBlocks: historyResponseBlock,
                    sqlSections: historySqlSections,
                    type: 'System',
                    source_used_for_response: record.source_used_for_response,
                    references: record.references,
                    runId: record?.run_id,
                    feedback: record?.feedback,
                    chartConfig,
                    toolResult,
                    ...((record?.model_used_for_response || record?.response_language) &&
                      models.current && {
                        modelUsed:
                          name === 'Image Generator'
                            ? record?.agent_metadata?.image_generation_agent?.image_model_name
                            : models?.current[
                                record?.model_used_for_response as keyof typeof models.current
                              ],
                        languageUsed: name === 'Image Generator' ? null : record?.response_language,
                      }),
                    ...(systemImageId != null && { imageId: systemImageId }),
                    ...(pptConfigObj && { pptConfig: pptConfigObj }),
                  }}
                  historySystemMessage
                />
              </div>
            );
          }}
        />
      )}
      {/*Active chat---> For Streaming + Active history*/}
      {isChatActive && combinedMessages.length > 0 && (
        <div>
          {combinedMessages.map((chatMsg, index) => (
            <ChatMessage key={index} message={chatMsg} />
          ))}
          <div ref={resultScrollRef} />
        </div>
      )}
      {/* Screen-reader support */}
      <div className="sr-only" aria-live="polite" role="status">
        {chatMessages.length > 0 && chatMessages[chatMessages.length - 1].type === 'System' && (
          <>
            Elysia Says:{' '}
            {(chatMessages[chatMessages.length - 1]?.toolResult?.length ?? 0) > 0 && (
              <>
                Searched: {(chatMessages[chatMessages.length - 1]?.toolResult?.length ?? 0) - 1}{' '}
                sites,
              </>
            )}
            <>
              <ReactMarkdown
                children={formatMarkdown(chatMessages[chatMessages.length - 1]?.body)}
              />
            </>
          </>
        )}
      </div>
      <div ref={scrollRef} />
    </>
  );
};

export default ChatSession;
